import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/auth'; // Import the useAuth hook
import { motion } from 'framer-motion';

const statusStyles = {
    "In-Progress": 'bg-yellow-200 text-yellow-800 py-1 px-2 text-xs font-semibold rounded',
    "Completed": 'bg-green-200 text-green-800 py-1 px-2 text-xs font-semibold rounded',
    "Rejected": 'bg-red-200 text-red-800 py-1 px-2 text-xs font-semibold rounded',
    "Submitted": 'bg-blue-200 text-blue-800 py-1 px-2 text-xs font-semibold rounded'
};

const MyApplications = () => {
    const [applications, setApplications] = useState([]);
    const [filteredApplications, setFilteredApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10; // Show only 5 forms per page
    const [auth] = useAuth(); // Use the Auth context to get user data
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get('http://192.168.1.50:5000/api/getAll/Form11');
                console.log('API Response:', response.data);

                if (Array.isArray(response.data)) {
                    setApplications(response.data);
                    if (auth.user && auth.user._id) {
                        const filtered = response.data.filter(item => item.user === auth.user._id);
                        setFilteredApplications(filtered);
                    } else {
                        setError('User information is not available');
                    }
                } else {
                    setError('Unexpected data format');
                }
                setLoading(false);
            } catch (err) {
                console.error('API Error:', err);
                setError('Failed to fetch applications');
                setLoading(false);
            }
        };

        fetchData();
    }, [auth]);

    const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredApplications.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleApplicationTypeClick = (applicationType, id) => {
        const trimmedApplicationType = applicationType.trim();
        console.log("Navigating to:", trimmedApplicationType, "with ID:", id);
        switch (trimmedApplicationType) {
            case "Voter Card":
                navigate(`/dashboard/application_votercard/${id}`);
                break;
            case "Shop Act":
                navigate(`/dashboard/application_shopact/${id}`);
                break;
            case "Company GST":
                navigate(`/dashboard/application_companygst/${id}`);
                break;
            case "Company Registration":
                navigate(`/dashboard/User_companyregistration/${id}`);
                break;
            case "Individual GST":
                navigate(`/dashboard/application_individualgst/${id}`);
                break;
            case "Local Food License":
                navigate(`/dashboard/User_localfoodlicense/${id}`);
                break;
            case "State Food License":
                navigate(`/dashboard/User_statefoodlicense/${id}`);
                break;
            case "Central Food License":
                navigate(`/dashboard/User_centralfoodlicense/${id}`);
                break;
            case "New VoterCard":
                navigate(`/dashboard/user_newvoter/${id}`);
                break;
            case "Food Manufacturing License":
                navigate(`/dashboard/User_foodmanufacturinglicense/${id}`);
                break;
            case "Domicile Certificate":
                navigate(`/dashboard/application_domicile/${id}`);
                break;
            case "GST Registration":
                navigate(`/dashboard/User_gstregistration/${id}`);
                break;
            case "Company Pancard":
                navigate(`/dashboard/User_companypancard/${id}`);
                break;
            case "Individual Pancard":
                navigate(`/dashboard/user_individualpancard/${id}`);
                break;
            case "Fresh Passport":
                navigate(`/dashboard/Application_passport_fresh/${id}`);
                break;
            case "Re-Issue Passport":
                navigate(`/dashboard/Application_passport_reissue/${id}`);
                break;
            case "Permanent License":
                navigate(`/dashboard/User_permanentlicense/${id}`);
                break;
            case "Learning License":
                navigate(`/dashboard/User_learninglicense/${id}`);
                break;
            case "Gazette Name Change":
                navigate(`/dashboard/User_gazettenamechange/${id}`);
                break;
            case "Gazette DOB Change":
                navigate(`/dashboard/User_gazettedobchange/${id}`);
                break;
            case "Gazette Religion Change":
                navigate(`/dashboard/User_gazettedreligionchange/${id}`);
                break;
            case "Shift Voter ID":
                navigate(`/dashboard/user_shiftvoter/${id}`);
                break;
            case "Udyam Aadhar":
                navigate(`/dashboard/user_udyam/${id}`);
                break;
            case "Income Certificate":
                navigate(`/dashboard/User_incomecertificate/${id}`);
                break;
            default:
                console.log("Unknown application type:", trimmedApplicationType);
        }
    };


    // Pagination: Generate page numbers dynamically based on the current page
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
    }

    const visiblePageNumbers = pageNumbers.slice(
        Math.max(0, currentPage - 3),
        Math.min(totalPages, currentPage + 2)
    );

    if (loading) return <div>Loading...</div>;
    if (error) return <div>{error}</div>;

    return (
        <div className="min-h-screen bg-slate-100 p-4 sm:p-6">
            <h2 className="mt-6 mb-6 ml-5 mr-5 text-2xl text-white text-center bg-blue-900 p-6 rounded-lg shadow-lg transition-transform duration-300">
                My Applications
            </h2>
            <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-x-auto w-full max-w-[21rem] sm:max-w-full"
            >
                <div className="container mx-auto bg-white rounded-lg shadow-lg p-5">
                    {/* <h1 className="text-2xl font-semibold text-gray-800 mb-6">My Applications</h1> */}
                    <div className="overflow-x-auto">
                        <table className="min-w-full table-auto border-collapse border border-gray-300">
                            <thead className="bg-gray-50">
                                <tr className='bg-gradient-to-r from-blue-500 to-blue-950'>
                                    <th className="px-4 py-3 text-left text-xs font-boldpy-2 border-2 border-[#3f4b74] text-white uppercase">Application Name</th>
                                    <th className="px-4 py-3 text-left text-xs font-bold border-2 border-[#3f4b74] text-white uppercase">First Name</th>
                                    <th className="px-4 py-3 text-left text-xs font-bold border-2 border-[#3f4b74] text-white uppercase">Last Name</th>
                                    <th className="px-4 py-3 text-left text-xs font-bold border-2 border-[#3f4b74] text-white uppercase">Email</th>
                                    {/* Removing hidden for Status and Updated Date */}
                                    <th className="px-4 py-3 text-left text-xs font-bold border-2 border-[#3f4b74] text-white uppercase">Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-bold border-2 border-[#3f4b74] text-white uppercase">Updated Date</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white">
                                {currentItems.map((app) => (
                                    <tr key={app._id} className="hover:bg-gray-100 hover:font-bold">
                                        <td
                                            className="px-4 py-3 font-semibold border-2 border-[#3f4b74] text-blue-600 cursor-pointer"
                                            onClick={() => handleApplicationTypeClick(app.application_type, app._id)}
                                        >
                                            {app.application_type}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-800 border-2 border-[#3f4b74]">{auth.user.firstname}</td>
                                        <td className="px-4 py-3 text-sm text-gray-800 border-2 border-[#3f4b74]">{auth.user.lastname}</td>
                                        <td className="px-4 py-3 text-sm text-gray-800 border-2 border-[#3f4b74]">{auth.user.email}</td>
                                        {/* Show Status and Updated Date */}
                                        <td className="px-4 py-3 text-sm text-gray-800 border-2 border-[#3f4b74]">
                                            <span className={statusStyles[app.Status]}>
                                                {app.Status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600 border-2 border-[#3f4b74]">{new Date(app.updatedAt).toLocaleDateString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    <div className="mt-6 flex justify-between items-center space-x-2">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-2 py-1 sm:px-4 sm:py-2 bg-blue-600  text-white rounded-lg shadow hover:bg-gray-300 hover:text-gray-700 disabled:opacity-50 text-xs sm:text-sm"
                        >
                            Previous
                        </button>

                        {/* Render page numbers */}
                        <div className="flex space-x-1 sm:space-x-2 overflow-x-auto">
                            {visiblePageNumbers.map((number) => (
                                <button
                                    key={number}
                                    onClick={() => handlePageChange(number)}
                                    className={`px-2 py-1 sm:px-4 sm:py-2 ${currentPage === number ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'} rounded-lg shadow hover:bg-gray-300 text-xs sm:text-sm`}
                                >
                                    {number}
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="px-2 py-1 sm:px-4 sm:py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-gray-300 hover:text-gray-700 disabled:opacity-50 text-xs sm:text-sm"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </motion.div >
        </div>

    );
};

export default MyApplications;
